import { useGetConversationSessionById } from "../../../hooks/use-conversation";
import { ArrowLeft, MessagesSquare } from "lucide-react";
import { parseAsString, useQueryStates, useQueryState } from "nuqs";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "components/ui/button";
import {
  AgentConversationSourceTrigger,
  AgentConversationDetailsSheet,
} from "./agent-conversation-source";
import { useConfig } from "hooks/use-config";
import { Skeleton } from "components/ui/skeleton";
import { MarkdownRenderer } from "../../markdown-renderer";

import { parseBackendUtc, formatFullDate } from "lib/utils";

type AgentConversationBoxProps = {
  agentId: string;
  title: string;
  username: string;
  isPrivate: boolean;
};

const EmptySession = () => {
  const { t } = useTranslation();

  return (
    <div className="flex h-full w-full flex-1 flex-col items-center justify-center rounded-sm bg-background p-4">
      <MessagesSquare className="h-12 w-12" />
      <p className="mt-3 text-lg font-medium text-high-emphasis">{t('SELECT_A_CONVERSATION')}</p>
    </div>
  );
};

const ConversationLoadingSkeleton = () => (
  <div className="flex max-h-[calc(100vh-300px)] flex-col gap-6 overflow-hidden p-4">
    <div className="flex justify-end">
      <div className="flex w-full max-w-[80%] flex-col items-end gap-2">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-16 w-full rounded-sm" />
      </div>
    </div>
    <div className="flex w-full flex-col gap-2">
      <Skeleton className="h-3 w-36" />
      <div className="space-y-2 rounded-sm bg-surface-app p-4">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-[92%]" />
        <Skeleton className="h-3 w-[78%]" />
      </div>
    </div>
    <div className="flex justify-end">
      <div className="flex w-full max-w-[55%] flex-col items-end gap-2">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-10 w-full rounded-sm" />
      </div>
    </div>
    <div className="flex w-full flex-col gap-2">
      <Skeleton className="h-3 w-36" />
      <div className="space-y-2 rounded-sm bg-surface-app p-4">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-[88%]" />
      </div>
    </div>
  </div>
);

const Separator = () => <span className="mx-2">●</span>;

export const AgentConversationBox = ({
  agentId,
  title,
  username,
  isPrivate,
}: AgentConversationBoxProps) => {
  const { t } = useTranslation();
  const [queryParams, setQueryParams] = useQueryStates({
    widget_id: parseAsString.withDefault(""),
    session_id: parseAsString.withDefault(""),
  });

  const [detailsPanel, setDetailsPanel] = useQueryState("details_panel");

  const { configurationSettings } = useConfig();
  const projectKey = configurationSettings?.blocksKey || "";
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useGetConversationSessionById({
    widget_id: queryParams?.widget_id,
    session_id: queryParams?.session_id,
    agent_id: agentId,
    limit: 100,
    offset: 0,
    project_key: projectKey,
    is_private: isPrivate,
    allow_created_by_filter: false,
    is_minimal: false,
  });

  const reversedSessions = useMemo(() => {
    const sessions = data?.sessions ?? [];

    return sessions
      .map((item) => ({
        ...item,
        Response: item.Response,
      }))
      .sort(
        (a, b) =>
          parseBackendUtc(a.QueryTimestamp).getTime() - parseBackendUtc(b.QueryTimestamp).getTime(),
      );
  }, [data]);

  const selectedSession = useMemo(() => {
    return reversedSessions.find((s) => s._id === detailsPanel) || null;
  }, [reversedSessions, detailsPanel]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [reversedSessions]);

  if (!queryParams.session_id) return <EmptySession />;

  return (
    <div className="h-full min-h-[500px] min-w-[380px] flex-1 rounded-sm bg-background">
      <div className="flex items-center gap-2 border p-4 md:hidden">
        <Button
          type="button"
          variant="ghost"
          className="h-fit w-fit px-1 py-0.5"
          onClick={() => {
            setQueryParams((prev) => ({
              ...prev,
              session_id: "",
            }));
          }}
        >
          <ArrowLeft className="aspect-square w-4" />
        </Button>
        <div className="truncate">{title}</div>
      </div>
      {isLoading ? (
        <ConversationLoadingSkeleton />
      ) : reversedSessions && reversedSessions.length > 0 ? (
        <div className="p-4">
          <div
            ref={scrollRef}
            className="flex max-h-[calc(100vh-300px)] flex-col gap-3 overflow-auto bg-background"
          >
            {reversedSessions.map((msg) => (
              <div key={msg?._id}>
                {msg?.Query?.trim() && (
                  <div className="flex justify-end">
                    <div className="flex min-w-0 max-w-[80%] flex-col items-end">
                      <div className="mb-1 text-xs font-semibold">
                        <time
                          className="mt-1 block text-xs text-low-emphasis"
                          dateTime={parseBackendUtc(msg?.QueryTimestamp).toISOString()}
                          title={parseBackendUtc(msg?.QueryTimestamp).toUTCString()}
                        >
                          <div className="flex flex-row">
                            <span>{username || t('YOU')}</span>
                            <Separator />
                            <span>
                              {formatFullDate(new Date(parseBackendUtc(msg?.QueryTimestamp)))}
                            </span>
                          </div>
                        </time>
                      </div>

                      <div className="mt-1 min-w-0 w-full overflow-hidden rounded-sm bg-surface-app p-4 text-sm text-medium-emphasis shadow-sm break-words [overflow-wrap:anywhere]">
                        {msg.Query}
                      </div>
                    </div>
                  </div>
                )}

                {msg?.Response && (
                  <div className="mt-4 w-full text-medium-emphasis">
                    <div className="mb-1 flex items-center justify-between text-xs font-semibold">
                      <time
                        className="mr-1 mt-1 block text-left text-xs text-low-emphasis"
                        dateTime={parseBackendUtc(msg?.ResponseTimestamp).toISOString()}
                        title={parseBackendUtc(msg?.ResponseTimestamp).toUTCString()}
                      >
                        <div className="flex flex-row">
                          <p className="text-high-emphasis">{t('AGENT')}</p>
                          <Separator />

                          <p className="font-normal text-low-emphasis">
                            {formatFullDate(new Date(parseBackendUtc(msg?.ResponseTimestamp)))}
                          </p>
                        </div>
                      </time>
                      {msg?.WorkflowTrace && (
                        <AgentConversationSourceTrigger
                          onClick={() => setDetailsPanel(msg._id)}
                          isOpen={detailsPanel === msg._id}
                        />
                      )}
                    </div>
                    <div className="mt-1 min-w-0 overflow-hidden rounded-sm bg-surface-app p-4 text-sm shadow-sm">
                      <MarkdownRenderer content={msg.Response} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <AgentConversationDetailsSheet
            session={selectedSession}
            isOpen={!!detailsPanel && !!selectedSession}
            onOpenChange={(open) => {
              if (!open) setDetailsPanel(null);
            }}
          />
        </div>
      ) : (
        <div className="flex h-full flex-col items-center justify-center text-medium-emphasis">
          <MessagesSquare className="h-12 w-12" />
          <p className="mt-3 text-lg font-medium">{t('NO_MESSAGES_IN_CONVERSATION')}</p>
        </div>
      )}
    </div>
  );
};
